import { FaroButtonContained } from "@components/common/faro-button-contained";
import { SPHERE_BUTTON_HEIGHT } from "@components/common/sphere-button";
import { BaseProjectProps } from "@custom-types/project-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { neutral } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Box, Skeleton } from "@mui/material";
import { appStateSelector } from "@store/app/app-selector";
import { isProjectArchiveSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { colorConst } from "@styles/common-colors";
import { withEllipsis } from "@styles/common-styles";
import { getOpenProjectHref, trackOpenProject } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { NavLink } from "react-router-dom";

interface Props extends BaseProjectProps {
  /** Flag to show a loading state */
  isLoading: boolean;
}

/**
 * Button to open the Viewer with the ?FromDmButton=true parameter.
 * Will be removed in NRT-2648.
 */
export function DataManagementButton({
  project,
  isLoading,
}: Props): JSX.Element | null {
  const { trackAsyncEvent } = useTrackEvent();

  const { canViewDataManagement } = useHasUserValidPermissionProjectLevel({
    project,
  });
  const isProjectArchived = useAppSelector(
    isProjectArchiveSelector(project.id)
  );

  const { isDevModeEnabled } = useAppSelector(appStateSelector);

  const hasRegularAccess = canViewDataManagement && !isProjectArchived;
  const isDevOnly = !hasRegularAccess && isDevModeEnabled;

  if (!hasRegularAccess && !isDevModeEnabled) {
    return null;
  } else if (isLoading) {
    return (
      <Box data-testid="sa-dm-skeleton">
        <Skeleton variant="rounded" width="122px" height="36px" sx={{ ml: "5px" }} />
      </Box>
    );
  }

  const sx = {
    ...(isDevOnly && {
      color: colorConst.devFeature,
      borderColor: colorConst.devFeature,
      "&:hover": {
        color: colorConst.devFeature,
        borderColor: colorConst.devFeature,
        backgroundColor: neutral[100],
        filter: "brightness(90%)",
      },
    }),
    height: SPHERE_BUTTON_HEIGHT,
    minWidth: "70px",
    gap: "8px",
    ml: "5px",
    alignItems: "center",
    ...withEllipsis,
  };

  const targetHref = getOpenProjectHref({
    openTarget: ProjectLaunchTarget.sphereViewer,
    projectId: project.id,
    additionalParams: new URLSearchParams({
      // [TF-2314] so in the Viewer we know we should start the tour
      FromDmButton: "true",
    }),
  });

  return (
    <NavLink to={targetHref}>
      <FaroButtonContained
        sx={sx}
        onClick={() => {
          void trackOpenProject({
            openFrom: "projectDetails-manageDataButton",
            numberOfMembers: project.members.length,
            openTarget: ProjectLaunchTarget.sphereViewer,
            trackAsyncEvent,
          });
        }}
      >
        Manage data
      </FaroButtonContained>
    </NavLink>
  );
}
